import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Shield, TrendingUp, Info, AlertTriangle, Target } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const STORAGE_KEY = 'emergency_fund_data';

const EmergencyFundCalculator = () => {
  const [monthlyExpenses, setMonthlyExpenses] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlyExpenses : 3000;
  });
  
  const [currentSavings, setCurrentSavings] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).currentSavings : 5000;
  });
  
  const [jobStability, setJobStability] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).jobStability : 'moderate';
  });
  
  const [desiredMonths, setDesiredMonths] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).desiredMonths : 6;
  });
  
  const [monthlySavings, setMonthlySavings] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlySavings : 500;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      monthlyExpenses,
      currentSavings,
      jobStability,
      desiredMonths,
      monthlySavings
    }));
  }, [monthlyExpenses, currentSavings, jobStability, desiredMonths, monthlySavings]);

  const getRecommendedMonths = () => {
    switch(jobStability) {
      case 'stable': return 3;
      case 'moderate': return 6;
      case 'risky': return 12;
      default: return 6;
    }
  };

  const calculateEmergencyFund = () => {
    const expenses = parseFloat(monthlyExpenses) || 0;
    const savings = parseFloat(currentSavings) || 0;
    const months = parseFloat(desiredMonths) || 0;
    const monthly = parseFloat(monthlySavings) || 0;
    
    const recommendedMonths = getRecommendedMonths();
    const requiredFund = expenses * months;
    const recommendedFund = expenses * recommendedMonths;
    const fundingGap = Math.max(0, requiredFund - savings);
    const monthsToGoal = monthly > 0 ? Math.ceil(fundingGap / monthly) : 0;
    const currentCoverage = expenses > 0 ? savings / expenses : 0;
    const progressPercent = requiredFund > 0 ? (savings / requiredFund) * 100 : 0;
    
    const riskLevel = currentCoverage < 3 ? 'High Risk' : 
                      currentCoverage < 6 ? 'Moderate Risk' : 'Low Risk';
    
    return {
      requiredFund,
      recommendedFund,
      fundingGap,
      monthsToGoal,
      currentCoverage,
      progressPercent,
      riskLevel,
      recommendedMonths
    };
  };

  const generateAccumulationData = () => {
    const savings = parseFloat(currentSavings) || 0;
    const monthly = parseFloat(monthlySavings) || 0;
    const results = calculateEmergencyFund();
    const monthsNeeded = Math.min(results.monthsToGoal + 2, 36);
    
    const data = [];
    for (let month = 0; month <= monthsNeeded; month++) {
      const accumulated = savings + (monthly * month);
      data.push({
        month,
        savings: Math.round(accumulated),
        goal: Math.round(results.requiredFund),
        recommended: Math.round(results.recommendedFund)
      });
    }
    return data;
  };

  const generateScenarioData = () => {
    const expenses = parseFloat(monthlyExpenses) || 0;
    const scenarios = [
      { name: '3 Months', months: 3 },
      { name: '6 Months', months: 6 },
      { name: '9 Months', months: 9 },
      { name: '12 Months', months: 12 }
    ];
    
    return scenarios.map(scenario => ({
      name: scenario.name,
      amount: expenses * scenario.months,
      current: parseFloat(currentSavings) || 0
    }));
  };

  const generateRiskImpact = () => {
    const expenses = parseFloat(monthlyExpenses) || 0;
    const risks = [
      { level: 'Stable Job', months: 3, amount: expenses * 3 },
      { level: 'Moderate Risk', months: 6, amount: expenses * 6 },
      { level: 'High Risk', months: 12, amount: expenses * 12 }
    ];
    return risks;
  };

  const results = calculateEmergencyFund();
  const accumulationData = generateAccumulationData();
  const scenarioData = generateScenarioData();
  const riskData = generateRiskImpact();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary-light/20 to-background">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-secondary flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Emergency Fund Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Shield className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Strategy
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is an Emergency Fund?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  An emergency fund is a financial safety net for unexpected expenses like medical bills, car repairs, or job loss. 
                  Financial experts typically recommend saving 3-12 months of living expenses, depending on your job stability and personal circumstances.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Your Information</CardTitle>
                  <CardDescription>Enter your financial details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="monthlyExpenses" className="text-sm font-medium">
                      Monthly Living Expenses ($)
                    </Label>
                    <Input
                      id="monthlyExpenses"
                      type="number"
                      value={monthlyExpenses}
                      onChange={(e) => setMonthlyExpenses(e.target.value)}
                      placeholder="3000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Essential costs: rent, food, utilities</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="currentSavings" className="text-sm font-medium">
                      Current Emergency Savings ($)
                    </Label>
                    <Input
                      id="currentSavings"
                      type="number"
                      value={currentSavings}
                      onChange={(e) => setCurrentSavings(e.target.value)}
                      placeholder="5000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="jobStability" className="text-sm font-medium">
                      Job Stability Level
                    </Label>
                    <Select value={jobStability} onValueChange={setJobStability}>
                      <SelectTrigger id="jobStability">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stable">Stable (Permanent, Low Risk)</SelectItem>
                        <SelectItem value="moderate">Moderate (Contract, Some Risk)</SelectItem>
                        <SelectItem value="risky">Risky (Freelance, High Volatility)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Affects recommended savings amount</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="desiredMonths" className="text-sm font-medium">
                      Desired Safety Period (Months)
                    </Label>
                    <Input
                      id="desiredMonths"
                      type="number"
                      value={desiredMonths}
                      onChange={(e) => setDesiredMonths(e.target.value)}
                      placeholder="6"
                      min="1"
                      max="24"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="monthlySavings" className="text-sm font-medium">
                      Monthly Savings Amount ($)
                    </Label>
                    <Input
                      id="monthlySavings"
                      type="number"
                      value={monthlySavings}
                      onChange={(e) => setMonthlySavings(e.target.value)}
                      placeholder="500"
                      className="text-base"
                    />
                  </div>

                  <Button 
                    className="w-full gradient-secondary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setMonthlyExpenses(monthlyExpenses)}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Calculate Fund
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-display">Emergency Fund Analysis</CardTitle>
                        <CardDescription>Your financial safety net status</CardDescription>
                      </div>
                      <Badge 
                        className={`px-4 py-2 text-sm ${
                          results.riskLevel === 'Low Risk' ? 'bg-secondary/20 text-secondary' :
                          results.riskLevel === 'Moderate Risk' ? 'bg-warning/20 text-warning' :
                          'bg-destructive/20 text-destructive'
                        }`}
                      >
                        {results.riskLevel}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Target className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Required Fund</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.requiredFund)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{desiredMonths} months coverage</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <Shield className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Current Coverage</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {results.currentCoverage.toFixed(1)} months
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{formatCurrency(parseFloat(currentSavings))}</p>
                      </div>

                      <div className={`p-6 rounded-xl bg-gradient-to-br ${
                        results.fundingGap === 0 ? 'from-secondary-light to-card border-secondary/20' : 'from-warning-light to-card border-warning/20'
                      } border`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${
                            results.fundingGap === 0 ? 'bg-secondary' : 'bg-warning'
                          } flex items-center justify-center`}>
                            <AlertTriangle className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Funding Gap</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${
                          results.fundingGap === 0 ? 'text-secondary' : 'text-warning'
                        }`}>
                          {formatCurrency(results.fundingGap)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {results.fundingGap === 0 ? 'Goal reached!' : 'Still needed'}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Time to Goal</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {results.monthsToGoal === 0 ? 'Complete' : `${results.monthsToGoal} months`}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">At {formatCurrency(parseFloat(monthlySavings))}/month</p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-muted-foreground">Progress to Goal</span>
                          <span className="text-sm font-semibold text-foreground">{results.progressPercent.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-3">
                          <div 
                            className="gradient-secondary h-3 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(results.progressPercent, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">Recommended for Your Risk</p>
                          <p className="text-xl font-display font-bold text-foreground">
                            {formatCurrency(results.recommendedFund)}
                          </p>
                          <p className="text-sm text-muted-foreground">{results.recommendedMonths} months coverage</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">Monthly Contribution</p>
                          <p className="text-xl font-display font-bold text-foreground">
                            {formatCurrency(parseFloat(monthlySavings))}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {parseFloat(monthlyExpenses) > 0 ? 
                              `${((parseFloat(monthlySavings) / parseFloat(monthlyExpenses)) * 100).toFixed(1)}% of expenses` :
                              'per month'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Savings Accumulation Timeline</CardTitle>
                <CardDescription className="text-base">
                  Track your progress to emergency fund goal
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={accumulationData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="month" 
                        label={{ value: 'Months', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="savings" 
                        stroke="hsl(var(--chart-2))" 
                        fill="hsl(var(--chart-2) / 0.3)"
                        strokeWidth={3}
                        name="Your Savings"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="goal" 
                        stroke="hsl(var(--chart-1))" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Your Goal"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="recommended" 
                        stroke="hsl(var(--chart-4))" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Recommended"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Fund Size Scenarios</CardTitle>
                  <CardDescription>
                    Different coverage periods vs your current savings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={scenarioData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="amount" fill="hsl(var(--chart-2))" name="Target Amount" />
                        <Bar dataKey="current" fill="hsl(var(--chart-1))" name="Current Savings" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Risk-Based Recommendations</CardTitle>
                  <CardDescription>
                    How job stability affects emergency fund size
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {riskData.map((risk, idx) => (
                      <div key={idx} className="p-4 rounded-lg bg-gradient-chart border border-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-foreground">{risk.level}</span>
                          <Badge variant="outline" className="text-xs">
                            {risk.months} months
                          </Badge>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-display font-bold text-secondary">
                            {formatCurrency(risk.amount)}
                          </span>
                        </div>
                        <div className="mt-3 w-full bg-muted rounded-full h-2">
                          <div 
                            className="gradient-secondary h-2 rounded-full"
                            style={{ width: `${Math.min((parseFloat(currentSavings) / risk.amount) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {parseFloat(currentSavings) >= risk.amount ? '✅ Goal achieved' : 
                           `${formatCurrency(risk.amount - parseFloat(currentSavings))} more needed`}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Emergency Fund Strategy & Insights</CardTitle>
                <CardDescription className="text-base">
                  Learn how to build and maintain your financial safety net
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Why You Need an Emergency Fund
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Life is unpredictable. An emergency fund protects you from financial disasters and prevents debt accumulation 
                    when unexpected expenses arise. It provides peace of mind and financial stability.
                  </p>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Job Loss Protection</h4>
                      <p className="text-muted-foreground">
                        Cover living expenses while searching for new employment without going into debt or depleting retirement savings.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Medical Emergencies</h4>
                      <p className="text-muted-foreground">
                        Handle unexpected medical bills, deductibles, or treatments not covered by insurance without financial stress.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Major Repairs</h4>
                      <p className="text-muted-foreground">
                        Pay for car repairs, home maintenance, or appliance replacements without using credit cards at high interest rates.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    How Much Should You Save?
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Based on your job stability of <strong className="text-foreground">{jobStability}</strong>, we recommend 
                    saving <strong className="text-primary">{results.recommendedMonths} months</strong> of expenses 
                    ({formatCurrency(results.recommendedFund)}).
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                    <div className="grid sm:grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Stable Job</p>
                        <p className="text-2xl font-display font-bold text-secondary">3-6 months</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Moderate Risk</p>
                        <p className="text-2xl font-display font-bold text-warning">6-9 months</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">High Risk</p>
                        <p className="text-2xl font-display font-bold text-destructive">9-12 months</p>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Building Your Emergency Fund
                  </h3>
                  <div className="space-y-3 text-base">
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Start Small, Think Big:</strong> If {formatCurrency(results.requiredFund)} seems 
                        overwhelming, start with a goal of $1,000. Then gradually build to one month of expenses, then three, and so on.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Automate Your Savings:</strong> Set up automatic transfers from checking to savings 
                        on payday. Treat your emergency fund contribution as a non-negotiable expense.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Use Windfalls Wisely:</strong> Tax refunds, bonuses, gifts, or other unexpected money 
                        should go directly to your emergency fund until it's fully funded.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Keep it Accessible but Separate:</strong> Store your emergency fund in a high-yield 
                        savings account - easily accessible but separate from your checking account to avoid temptation.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Replenish After Use:</strong> If you need to use your emergency fund, make rebuilding 
                        it a priority. Resume your automatic contributions as soon as possible.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-warning">
                    Common Mistakes to Avoid
                  </h3>
                  <div className="space-y-3 text-base">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Investing Emergency Funds:</strong> Don't invest your emergency fund in stocks or 
                        other volatile assets. You need guaranteed access when emergencies strike, not market-dependent returns.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Using it for Non-Emergencies:</strong> A vacation, new TV, or holiday shopping are not 
                        emergencies. Define what constitutes an emergency and stick to it.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Delaying Because of Debt:</strong> Even if you have debt, build at least a $1,000 
                        emergency fund first. Otherwise, new emergencies will create more debt.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Your Action Plan
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                      <p className="text-muted-foreground">Save {formatCurrency(parseFloat(monthlySavings))} per month</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <p className="text-muted-foreground">Reach your goal of {formatCurrency(results.requiredFund)} in {results.monthsToGoal} months</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">3</span>
                      </div>
                      <p className="text-muted-foreground">Enjoy peace of mind with {desiredMonths} months of expenses covered</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EmergencyFundCalculator;
